import React, { useState, useEffect } from 'react';
import LineDescription from '../components/documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC } from '../components/documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
import { formatDisplayAddress, buildAddressString } from '../utils/address'; // marker b4d8f2a6 + e6a2f5b9 -- buildAddressString formats the billTo block (address1/address2 keys)
import { normalizeBillTo, BILLTO_MODES } from '../utils/billTo'; // marker e6a2f5b9 -- Seam 2: resolve/gate the bill-to party on-screen
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';
import { 
    Loader2, CreditCard, AlertCircle, 
    FileText, X, Download, ArrowLeft, CheckCircle 
} from 'lucide-react'; 
import InvoiceStatusBadge from '../components/common/InvoiceStatusBadge';
import PaymentModal from '../components/common/PaymentModal';
import PortalPayAllModal from '../components/common/PortalPayAllModal'; // Seam 11 Part B
import { formatCurrency } from '../utils/formatters';
import { importedLinePrice } from '../utils/importedLine'; // impprice-5d2a8c46
import { downloadPdfBytes } from '../utils/pdfDownload'; // docpdf-8a4c1e73 — PDFs as bytes; nothing stored, no link
import { customerTaxRows, taxRowText } from '../utils/lineTax'; // taxlines2-3e0b16a4 — one row per tax part when the stored parts add up; taxexempt3-8c4f2a1d — taxRowText

const PortalInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    // Seam 11 Part A/B: the customer's account position (projected by
    // getCustomerInvoices) + the Pay All modal open state.
    const [accountSummary, setAccountSummary] = useState({ totalOutstanding: 0, unappliedCredit: 0 });
    const [isPayAllOpen, setIsPayAllOpen] = useState(false);
    // payhide-7e4c1b05 — true while the org can't take online payments (server-computed
    // !canOrgTakeOnlinePayments via getPublicOrganizationInfo). Starts hidden; the page shows
    // a spinner until both fetches settle, so there is no flash. If the org fetch FAILS, Pay
    // shows: the server still refuses cleanly, and hiding Pay from an org that can take
    // payments would strand its customer.
    const [onlinePayHidden, setOnlinePayHidden] = useState(true);

    // State
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [viewingInvoice, setViewingInvoice] = useState(null);
    
    // PDF State
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    // --- HELPER: CHECK IF OVERDUE ---
    // PHASE 1.5.3 VERIFIER: r1-153-portal-view-a7b2d9f4
    // dueDate is now an ISO string (from sanitized CF), not a Firestore Timestamp
    const isInvoiceOverdue = (invoice) => {
        if (invoice.status === 'Paid') return false;
        if (invoice.status === 'Written Off') return false; // s0bwoff-f74fb923: bad debt is never "overdue"
        if (invoice.status === 'Past Due') return true;
        
        const today = new Date();
        today.setHours(0,0,0,0); 
        
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
        return dueDate && dueDate < today;
    };

    useEffect(() => {
        const fetchInvoices = async () => {
            const user = auth.currentUser;
            if (!user) return;

            try {
                // PHASE 1.5.3 VERIFIER: r1-153-portal-view-a7b2d9f4
                // Sanitized callable replaces direct Firestore read. The CF projects
                // a strict customer-safe whitelist (no invoice.notes, no recordedBy
                // UIDs, etc) and merges customer-facing notes data server-side.
                // The CF reads orgId/customerId from auth-token claims — no need
                // to pass them in.
                const getInvoices = httpsCallable(functions, 'getCustomerInvoices');
                const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                const [invRes, orgRes] = await Promise.allSettled([getInvoices(), getOrgInfo()]);
                setOnlinePayHidden(orgRes.status === 'fulfilled' && orgRes.value.data?.onlinePayHidden === true);
                if (invRes.status === 'rejected') throw invRes.reason;
                const result = invRes.value;
                const fetchedInvoices = (result.data && Array.isArray(result.data.invoices))
                    ? result.data.invoices
                    : [];

                // Seam 11 Part A: account position (display only).
                if (result.data && result.data.accountSummary) {
                    setAccountSummary({
                        totalOutstanding: Number(result.data.accountSummary.totalOutstanding) || 0,
                        unappliedCredit: Number(result.data.accountSummary.unappliedCredit) || 0,
                    });
                }

                // Smart Sort: Overdue -> Unpaid -> Date (createdAt is now an ISO string)
                fetchedInvoices.sort((a, b) => {
                    const aOverdue = isInvoiceOverdue(a);
                    const bOverdue = isInvoiceOverdue(b);
                    if (aOverdue && !bOverdue) return -1;
                    if (!aOverdue && bOverdue) return 1;
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return dateB - dateA;
                });

                setInvoices(fetchedInvoices);
            } catch (error) {
                console.error("Error fetching invoices:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
    }, []);

    const handlePayClick = (e, invoice) => {
        if (e) e.stopPropagation(); 
        setSelectedInvoice(invoice);
        setIsPaymentModalOpen(true);
    };

    // docpdf-8a4c1e73 — the PDF comes back as bytes and is saved in this browser: nothing is stored on the
    // server and there is no link (was a file in Storage opened through a link, in a pre-opened tab).
    const handleDownloadPdf = async (invoice) => {
        setIsDownloadingPdf(true);
        try {
            const result = await httpsCallable(functions, 'getPortalInvoicePdf', { timeout: 120000 })({ invoiceId: invoice.id });
            downloadPdfBytes(result.data?.pdfBase64, result.data?.fileName);
        } catch (error) {
            console.error("PDF Download failed:", error);
            alert(`Could not download PDF. ${error.message || "Unknown error"}`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // cnsummary-3e9a6d51 — R1c-3: download one of the customer's credit notes (the server checks the
    // note is theirs). docpdf-8a4c1e73 — bytes, saved in this browser, like the invoice PDF above.
    const handleDownloadCreditNote = async (credit) => {
        setIsDownloadingPdf(true);
        try {
            const result = await httpsCallable(functions, 'getPortalCreditNotePdf', { timeout: 120000 })({ creditNoteId: credit.creditNoteId });
            downloadPdfBytes(result.data?.pdfBase64, result.data?.fileName);
        } catch (error) {
            console.error("Credit note download failed:", error);
            alert(`Could not download the credit note. ${error.message || "Unknown error"}`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

// --- ROBUST INVOICE DETAIL MODAL ---
    const InvoiceDetailModal = ({ invoice, onClose }) => {
        const [orgInfo, setOrgInfo] = useState({ taxId: null, currency: 'USD' });

        useEffect(() => {
            const fetchInfo = async () => {
                try {
                    // Use the secure Cloud Function instead of direct DB read
                    const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                    const result = await getOrgInfo();
                    setOrgInfo(result.data);
                } catch (error) {
                    console.error("Failed to load org info:", error);
                }
            };
            fetchInfo();
        }, []);

        if (!invoice) return null;
        
        const isOverdue = isInvoiceOverdue(invoice);
        const taxRows = customerTaxRows(invoice, 'headline'); // taxlines2-3e0b16a4 — null → today's single Tax row
        // sdrefund3-portal-6f3c1d84 -- read stored amountPaid with ?? (not ||) so a
        // full refund's stored 0 is respected instead of being re-summed away.
        const amountPaid = invoice.amountPaid ??
            (invoice.paymentHistory ? invoice.paymentHistory.reduce((acc, p) => acc + (Number(p.amount) || 0), 0) : 0);
        
        // Ensure balance is never negative visually
        let balanceDue = invoice.balanceDue !== undefined ? invoice.balanceDue : (invoice.total - amountPaid);
        if (balanceDue < 0.01) balanceDue = 0;
        // cnsummary-3e9a6d51 — R1c-3: set by getCustomerInvoices ONLY for an invoice carrying credit
        // notes (the shared rule, utils/invoiceCreditSummary.js); every other invoice renders as before.
        const creditSummary = invoice.creditSummary || null;

        // Use Currency from Org Settings if available, else default
        const currencyCode = orgInfo.currency || 'USD';
        // marker f1c8d3a4 -- Seam 2: resolve the bill-to party once (mirror of the
        // server generateInvoiceHtml). null (absent/malformed) => no BILL TO block.
        // billTo address uses buildAddressString (address1/address2 keys), suppressed
        // for a propertyContact when propertyId is set (already under Service Location).
        const billTo = normalizeBillTo(invoice.billTo);
        const billToAddress = (billTo && !(billTo.mode === BILLTO_MODES.PROPERTY_CONTACT && invoice.propertyId))
            ? buildAddressString(billTo)
            : '';

        // Local helper to force currency match
        const localFormatCurrency = (amount) => {
            return Number(amount || 0).toLocaleString('en-US', { style: 'currency', currency: currencyCode });
        };

        return (
            <div className="fixed inset-0 bg-gray-100 z-50 overflow-y-auto animate-in fade-in duration-200">
                {/* --- TOP BAR --- */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 md:px-8 flex justify-between items-center z-10 shadow-sm">
                    <button 
                        onClick={onClose} 
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Back
                    </button>
                    
                    <button 
                        onClick={() => handleDownloadPdf(invoice)}
                        disabled={isDownloadingPdf}
                        className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                    >
                        {isDownloadingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        <span className="hidden sm:inline">Download PDF</span>
                    </button>
                </div>

                <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
                    
                    {/* --- MAIN INVOICE CARD --- */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        {/* Header Section */}
                        <div className="p-8 border-b border-gray-100">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Invoice #{invoice.invoiceNumber}</h2>
                                    <h1 className="text-3xl font-bold text-gray-900">{orgInfo.companyName || 'Invoice'}</h1>
                                    {(orgInfo.address1 || orgInfo.city || orgInfo.phone) && (
                                        <div className="mt-1 text-sm text-gray-500">
                                            {orgInfo.address1 && <p>{orgInfo.address1}{orgInfo.address2 ? `, ${orgInfo.address2}` : ''}</p>}
                                            {(orgInfo.city || orgInfo.state || orgInfo.zip) && (
                                                <p>{[orgInfo.city, orgInfo.state, orgInfo.zip].filter(Boolean).join(', ')}</p>
                                            )}
                                            {orgInfo.phone && <p>{orgInfo.phone}</p>}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <InvoiceStatusBadge status={invoice.status} />
                                    {isOverdue && (
                                        <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">OVERDUE</span>
                                    )}
                                </div>
                            </div>

                            {/* Addresses & Dates Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                                {/* Left: Bill To */}
                                <div>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">Customer</h3>{/* marker a8c5e2f7 -- Seam 2 relabel Billed To -> Customer */}
                                    <p className="font-bold text-gray-900 text-lg">{invoice.customerDetails?.name || invoice.customerName}</p>
                                    {(() => {
                                        // marker c9e15a30 -- format first (handles object-shaped imported
                                        // addresses), then gate on the resulting STRING so an object or
                                        // empty address renders no empty <p> (fixes the blank-page crash).
                                        const billedToAddress = formatDisplayAddress(invoice.customerDetails?.address);
                                        return billedToAddress ? (
                                            <p className="text-gray-600 whitespace-pre-line text-sm mt-1">{billedToAddress}</p>
                                        ) : null;
                                    })()}
                                    {invoice.customerDetails?.email && (
                                        <p className="text-gray-600 text-sm mt-1">{invoice.customerDetails.email}</p>
                                    )}
                                    {/* marker b2d9f4a1 -- Seam 2: BILL TO block, only when a bill-to party is set.
                                        Between CUSTOMER and Service Location, mirroring the PDF order. */}
                                    {billTo && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase mb-1">Bill To</h4>
                                            <p className="font-bold text-gray-900 text-sm">{billTo.name || ''}</p>
                                            {billTo.company && <p className="text-gray-600 text-sm">{billTo.company}</p>}
                                            {billToAddress && <p className="text-gray-600 whitespace-pre-line text-sm">{billToAddress}</p>}
                                            {billTo.email && <p className="text-gray-600 text-sm">{billTo.email}</p>}
                                        </div>
                                    )}
                                    {/* PROPERTIES v3 — Service location (only when the invoice is tied to a property) */}
                                    {invoice.propertyId && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase mb-1">Service Location</h4>
                                            <p className="font-semibold text-gray-800 text-sm">{invoice.propertyName || 'Property'}</p>
                                            {invoice.serviceAddress && (
                                                <p className="text-gray-600 whitespace-pre-line text-sm">{invoice.serviceAddress}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Right: Dates */}
                                <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500 text-sm">Issued</span>
                                        <span className="font-medium text-gray-900">{invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : ''}</span>
                                    </div>
                                    <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                                        <span className="text-gray-500 text-sm">Due</span>
                                        <span className="font-medium text-gray-900">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : ''}</span>
                                    </div>
                                    {(() => {
                                        // recinv-billperiod-6b1f2a9c -- Billing Period, ONLY when the recurring
                                        // generator stamped both bounds. Absent on ordinary invoices → renders
                                        // nothing. The portal receives ISO STRINGS from getCustomerInvoices
                                        // (toIsoString projection), so coerce with new Date(x) — NOT .toDate().
                                        // Formatting mirror: generateInvoiceHtml (functions/index.js ~2160) +
                                        // InvoiceDetailsView marker recinv-billperiod-7c1f9a04. Keep all three in step.
                                        const s = invoice.billingPeriodStart ? new Date(invoice.billingPeriodStart) : null;
                                        const e = invoice.billingPeriodEnd ? new Date(invoice.billingPeriodEnd) : null;
                                        if (!s || !e) return null;
                                        const sameYear = s.getFullYear() === e.getFullYear();
                                        const startStr = s.toLocaleDateString('en-US', sameYear
                                            ? { month: 'short', day: 'numeric' }
                                            : { month: 'short', day: 'numeric', year: 'numeric' });
                                        const endStr = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                        return (
                                            <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                                                <span className="text-gray-500 text-sm">Billing Period</span>
                                                <span className="font-medium text-gray-900">{startStr} – {endStr}</span>
                                            </div>
                                        );
                                    })()}
                                    {invoice.paidAt && (
                                        <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                                            <span className="text-gray-500 text-sm">Paid On</span>
                                            <span className="font-medium text-green-700">{new Date(invoice.paidAt).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Line Items Table */}
                        <div className="p-8">
                            <h3 className="text-sm font-bold text-gray-900 mb-4 border-b pb-2">Product / Service</h3>
                            <div className="space-y-4">
                                {invoice.lineItems?.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
                                        <div className="flex-1 min-w-0 pr-4 break-words">
                                            <p className={LINE_NAME}>{item.name}</p>
                                            {item.description && <span className={LINE_DESC}><LineDescription text={item.description} /></span>}
                                        </div>
                                        <div className="text-right flex-shrink-0 w-32">
                                            {/* impprice-5d2a8c46 — imported lines store unitPrice */}
                                            <div className="text-xs text-gray-400 mb-1">{item.quantity} x {localFormatCurrency(importedLinePrice(item))}</div>
                                            <div className="font-medium text-gray-900">{localFormatCurrency((item.quantity || 0) * importedLinePrice(item))}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Financial Summary */}
                        <div className="bg-gray-50 p-8 border-t border-gray-100">
                            <div className="flex justify-end">
                                <div className="w-full md:w-1/2 space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium text-gray-900">{localFormatCurrency(invoice.subtotal || 0)}</span>
                                    </div>
                                    {/* taxlines2-3e0b16a4 — one row per tax part when the stored parts add up to the tax; else today's row */}
                                    {taxRows ? taxRows.map((r, i) => (
                                        <div key={`tax-${i}`} className="flex justify-between text-sm">
                                            <span className="text-gray-600">{taxRowText(r)}</span>
                                            <span className="font-medium text-gray-900">{localFormatCurrency(r.amount)}</span>
                                        </div>
                                    )) : (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Tax</span>
                                        <span className="font-medium text-gray-900">{localFormatCurrency(invoice.taxAmount || 0)}</span>
                                    </div>
                                    )}
                                    
                                    <div className="border-t border-gray-200 my-2"></div>
                                    
                                    <div className="flex justify-between items-center">
                                        <span className="text-base font-bold text-gray-700">Total</span>
                                        <span className="text-xl font-bold text-gray-900">{localFormatCurrency(invoice.total || 0)}</span>
                                    </div>

                                    {creditSummary ? (
                                        // cnsummary-3e9a6d51 — credit notes, then "Net paid" (no minus), then the balance.
                                        invoice.creditSummary.lines.map((line, idx) => (
                                            line.kind === 'balance' ? (
                                                <div key={idx} className="flex justify-between items-center pt-2 border-t border-gray-300 mt-2">
                                                    <span className="text-base font-bold text-gray-900">Balance Due</span>
                                                    <span className={`text-2xl font-bold ${line.paidInFull ? 'text-green-700' : 'text-gray-900'}`}>
                                                        {line.paidInFull ? 'Paid in full' : localFormatCurrency(line.amount)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div key={idx} className={`flex justify-between text-sm ${line.kind === 'credit' ? 'text-green-700' : 'text-gray-700'}`}>
                                                    <span className="flex items-center gap-1">{line.kind === 'netPaid' && <CheckCircle size={12}/>} {line.label}</span>
                                                    <span className="font-medium">{line.kind === 'credit' ? '-' : ''}{localFormatCurrency(line.amount)}</span>
                                                </div>
                                            )
                                        ))
                                    ) : (
                                    <>
                                    {amountPaid > 0 && (
                                        <div className="flex justify-between text-sm text-green-700">
                                            <span className="flex items-center gap-1"><CheckCircle size={12}/> Paid to date</span>
                                            <span className="font-medium">-{localFormatCurrency(amountPaid)}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-center pt-2 border-t border-gray-300 mt-2">
                                        <span className="text-base font-bold text-gray-900">Balance Due</span>
                                        <span className="text-2xl font-bold text-gray-900">{localFormatCurrency(balanceDue)}</span>
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer / Tax ID */}
                        <div className="p-8 border-t border-gray-100 text-sm text-gray-500">
                            <p className="mb-2">Thank you for your business. Please contact us with any questions regarding this invoice.</p>
                            
                            {orgInfo.taxId && (
                                <p className="mt-4 pt-4 border-t border-gray-100 font-medium text-gray-400">
                                    Tax ID / GST / HST: <span className="text-gray-600">{orgInfo.taxId}</span>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* --- CUSTOMER-FACING NOTES SECTION --- */}
                    {/* PHASE 1.5.3 VERIFIER: r1-153-portal-view-a7b2d9f4
                        Replaces the round-1 hotfix that rendered invoice.workPerformed
                        directly. Now consumes invoice.customerFacingNotes.items from the
                        sanitized CF (live-merge of polymorphic notes + legacy techNotes
                        + back-compat synthesis from workPerformed for pre-Phase-1.1 data).
                        invoice.notes never reaches this client. */}
                    {invoice.customerFacingNotes?.items?.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                <FileText size={20} className="text-gray-400"/> Notes from your service provider
                            </h3>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden divide-y divide-gray-100">
                                {invoice.customerFacingNotes.items.map((note, idx) => {
                                    const dateLabel = note.addedAtEpoch
                                        ? new Date(note.addedAtEpoch).toLocaleDateString()
                                        : '';
                                    const byline = note.authorName
                                        ? `${note.authorName}${dateLabel ? ` · ${dateLabel}` : ''}`
                                        : dateLabel;
                                    // r1-3g-invoice-notes-photogrid-8c4a1e2f — split photos into a
                                    // responsive grid (mirrors quote card r1-1bc-notescard-2f7d9e41);
                                    // non-photo media keeps its text-link fallback. Strict type==='photo'
                                    // filter now matches mobile photos correctly since r1-3h.
                                    const photos = Array.isArray(note.media)
                                        ? note.media.filter(m => m && m.type === 'photo' && m.url)
                                        : [];
                                    const otherMedia = Array.isArray(note.media)
                                        ? note.media.filter(m => m && m.url && m.type !== 'photo')
                                        : [];
                                    return (
                                        <div key={idx} className="p-6">
                                            {note.text && (
                                                <p className="text-gray-700 whitespace-pre-line">{note.text}</p>
                                            )}
                                            {photos.length > 0 && (
                                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                    {photos.map((m, midx) => (
                                                        <a
                                                            key={midx}
                                                            href={m.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="block rounded-md overflow-hidden border border-gray-200 bg-white"
                                                        >
                                                            <img
                                                                src={m.thumbUrl || m.url}
                                                                alt="Service photo"
                                                                loading="lazy"
                                                                className="w-full h-28 object-cover"
                                                            />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            {otherMedia.length > 0 && (
                                                <div className="mt-3 flex flex-wrap gap-3">
                                                    {otherMedia.map((m, midx) => {
                                                        if (m.type === 'video') {
                                                            return (
                                                                <a key={midx} href={m.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                                                                    ▶ View video
                                                                </a>
                                                            );
                                                        }
                                                        if (m.type === 'voice') {
                                                            return (
                                                                <a key={midx} href={m.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                                                                    🎤 Listen to voice note
                                                                </a>
                                                            );
                                                        }
                                                        return (
                                                            <a key={midx} href={m.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                                                                View attachment
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {byline && (
                                                <p className="text-xs text-gray-400 mt-3">{byline}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* cnsummary-3e9a6d51 — R1c-3: CREDIT NOTES under their invoice, each with Download. */}
                    {creditSummary && creditSummary.credits.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                <FileText size={20} className="text-gray-400"/> Credit Notes
                            </h3>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                {creditSummary.credits.map((credit, idx) => (
                                    <div key={credit.creditNoteId || idx} className="p-4 flex items-center justify-between border-b last:border-0">
                                        <div>
                                            <p className="font-bold text-gray-900">Credit note {credit.number} · {localFormatCurrency(credit.amount)}</p>
                                            {credit.date && <p className="text-sm text-gray-500">{new Date(credit.date).toLocaleDateString()}</p>}
                                        </div>
                                        {credit.creditNoteId && (
                                            <button
                                                onClick={() => handleDownloadCreditNote(credit)}
                                                disabled={isDownloadingPdf}
                                                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                                            >
                                                <Download size={14} /> Download
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* cnsummary-3e9a6d51 — with credit notes, receipts come from the server summary:
                        no internal credit-note line, each refund named ("Refund by cheque (credit note CN-0009)"). */}
                    {creditSummary && creditSummary.payments.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                <CreditCard size={20} className="text-gray-400"/> Receipts
                            </h3>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                {invoice.creditSummary.payments.map((payment, idx) => (
                                    <div key={idx} className="p-4 flex items-center justify-between border-b last:border-0 hover:bg-gray-50 transition-colors">
                                        <div>
                                            <p className="font-bold text-gray-900">{localFormatCurrency(payment.amount)} {payment.isRefund ? 'Refund' : 'Payment'}</p>
                                            <p className="text-sm text-gray-500">
                                                {payment.date ? new Date(payment.date).toLocaleDateString() : ''} • {payment.label}
                                            </p>
                                        </div>
                                        {payment.isRefund ? (
                                            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold uppercase rounded-full">Refunded</span>
                                        ) : (
                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase rounded-full">Success</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* --- RECEIPTS SECTION --- */}
                    {!creditSummary && invoice.paymentHistory && invoice.paymentHistory.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                <CreditCard size={20} className="text-gray-400"/> Receipts
                            </h3>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                {invoice.paymentHistory.map((payment, idx) => {
                                    // sdrefund3-portal-6f3c1d84 -- a refund reads as a refund on
                                    // the customer's portal: "Refund", a POSITIVE amount (Math.abs),
                                    // and an amber "Refunded" badge -- never a negative figure and
                                    // never a green Success badge. A payment renders as before.
                                    const isRefundRow = payment.isRefund || Number(payment.amount) < 0;
                                    return (
                                    <div key={idx} className="p-4 flex items-center justify-between border-b last:border-0 hover:bg-gray-50 transition-colors">
                                        <div>
                                            <p className="font-bold text-gray-900">{localFormatCurrency(Math.abs(Number(payment.amount) || 0))} {isRefundRow ? 'Refund' : 'Payment'}</p>
                                            <p className="text-sm text-gray-500">
                                                {new Date(payment.date).toLocaleDateString()} • {payment.method} {payment.reference ? `• ${payment.reference}` : ''}
                                            </p>
                                        </div>
                                        {isRefundRow ? (
                                            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold uppercase rounded-full">
                                                Refunded
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase rounded-full">
                                                Success
                                            </span>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Bottom Action Bar (Mobile Only) */}
                    {invoice.status !== 'Paid' && invoice.status !== 'Written Off' && !onlinePayHidden && ( /* s0bwoff-f74fb923 */
                        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg md:hidden">
                             <button 
                                onClick={(e) => {
                                    handlePayClick(e, invoice);
                                }}
                                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-md shadow-sm transition-colors"
                            >
                                Pay {localFormatCurrency(balanceDue)}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-blue-600" /></div>;

    return (
        <div className="max-w-4xl">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
                    <p className="text-gray-500 mt-1">{onlinePayHidden ? 'View your outstanding balances' : 'View and pay your outstanding balances'}</p>
                </div>
            </div>

            {/* payall-8d5c1f37 -- Seam 11 Part A/B: account position + Pay All. Total
                Outstanding is always shown; Account Credit is information only (customers
                never apply credit -- that's operator-controlled). Pay All charges the full
                outstanding in one payment. */}
            {(accountSummary.totalOutstanding > 0.01 || accountSummary.unappliedCredit > 0) && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex flex-col sm:flex-row gap-6">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">Total Outstanding</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(accountSummary.totalOutstanding)}</p>
                            </div>
                            {accountSummary.unappliedCredit > 0 && (
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Account Credit</p>
                                    <p className="text-2xl font-bold text-[#5F4EC9]">{formatCurrency(accountSummary.unappliedCredit)}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">applied to your invoices by your service provider</p>
                                </div>
                            )}
                        </div>
                        {accountSummary.totalOutstanding > 0.01 && !onlinePayHidden && (
                            <button
                                onClick={() => setIsPayAllOpen(true)}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-md shadow-sm transition-colors whitespace-nowrap"
                            >
                                Pay All — {formatCurrency(accountSummary.totalOutstanding)}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {invoices.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="text-gray-400" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No invoices found</h3>
                    <p className="text-gray-500 mt-1">You are all caught up!</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {invoices.map((invoice) => {
                        const isOverdue = isInvoiceOverdue(invoice);
                        
                        return (
                            <div 
                                key={invoice.id}
                                onClick={() => setViewingInvoice(invoice)} 
                                className={`bg-white rounded-lg shadow-sm border transition-all cursor-pointer ${isOverdue ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
                            >
                                <div className="p-6 flex flex-col md:flex-row md:items-center justify-between">
                                    <div className="mb-4 md:mb-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <FileText size={18} className="text-gray-400" />
                                                #{invoice.invoiceNumber || invoice.id.slice(0,6).toUpperCase()}
                                            </h3>
                                            <InvoiceStatusBadge status={invoice.status} />
                                            
                                            {isOverdue && (
                                                <span className="flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded-full animate-pulse">
                                                    <AlertCircle size={12} /> OVERDUE
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-gray-500 text-sm ml-7">
                                            <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                                                Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'Upon Receipt'}
                                            </span>
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Amount Due</p>
                                            <p className={`text-xl font-bold ${isOverdue ? 'text-red-700' : 'text-gray-900'}`}>
                                                {formatCurrency ? formatCurrency(invoice.balanceDue !== undefined ? invoice.balanceDue : invoice.total) : `$${(parseFloat(invoice.total) || 0).toFixed(2)}`}
                                            </p>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <button 
                                                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors"
                                            >
                                                View
                                            </button>

                                            {invoice.status !== 'Paid' && invoice.status !== 'Written Off' && !onlinePayHidden && ( /* s0bwoff-f74fb923 */
                                                <button 
                                                    onClick={(e) => handlePayClick(e, invoice)}
                                                    className={`${isOverdue ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-semibold py-2 px-4 rounded-md transition-colors shadow-sm`}
                                                >
                                                    Pay Now
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* FULL SCREEN DETAIL MODAL */}
            {viewingInvoice && (
                <InvoiceDetailModal 
                    invoice={viewingInvoice} 
                    onClose={() => setViewingInvoice(null)} 
                />
            )}

            {/* PAYMENT MODAL */}
            {selectedInvoice && (
                <PaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => setIsPaymentModalOpen(false)}
                    invoice={selectedInvoice}
                    currentUserProfile={{}}
                    portalMode={true}
                />
            )}

            {/* Seam 11 Part B: Pay All. On success, reload so every
                affected invoice + the summary reflect the spread (mirrors PaymentModal). */}
            <PortalPayAllModal
                isOpen={isPayAllOpen}
                onClose={() => setIsPayAllOpen(false)}
                onSuccess={() => window.location.reload()}
            />
        </div>
    );
};

export default PortalInvoices;